import { useCallback, useSyncExternalStore } from "react";
import { isServer } from "src/shared/utils/ssrUtils";

const STORAGE_KEY = "aip_bottomsheet_last_shown";
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

// Cross-tab writes are the only external changes to this value — same-tab
// writes (markShown) don't need to notify, since callers stop gating on it
// once they've fired.
const subscribe = (onStoreChange: () => void) => {
  window.addEventListener("storage", onStoreChange);
  return () => window.removeEventListener("storage", onStoreChange);
};

const getSnapshot = (): boolean => {
  try {
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (!lastShown) return true;
    return Date.now() - Number(lastShown) >= TWENTY_FOUR_HOURS;
  } catch {
    return true;
  }
};

// null signals "not yet resolved" — matches the server-rendered HTML so
// hydration can't mismatch, then React re-renders with the real client value.
const getServerSnapshot = (): null => null;

export const useAipBottomsheetFrequency = () => {
  const resolved = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const markShown = useCallback(() => {
    if (isServer()) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, []);

  return { shouldShowBottomsheet: resolved ?? false, isFrequencyResolved: resolved !== null, markShown };
};
